import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

/**
 * <FoodCarousel /> — auto-sliding, touch-enabled Embla carousel.
 * Click any slide → lightbox opens with cinematic zoom.
 */
export default function FoodCarousel({ items = [], onOpen }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    dragFree: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);

  // Auto-play (cinematic pacing)
  useEffect(() => {
    if (!emblaApi) return;
    const id = setInterval(() => emblaApi.scrollNext(), 4200);
    const onSel = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSel);
    };
  }, [emblaApi]);

  return (
    <div className="relative" data-testid="food-carousel">
      <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
        <div className="flex">
          {items.map((it, i) => (
            <div
              key={it.id}
              className="min-w-0 shrink-0 grow-0 basis-[92%] sm:basis-[70%] md:basis-[52%] lg:basis-[44%] pr-3 md:pr-4"
            >
              <motion.button
                type="button"
                onClick={() =>
                  onOpen &&
                  onOpen({
                    src: it.img,
                    title: it.name,
                    subtitle: it.subtitle,
                    alt: `${it.name} — ${it.subtitle}`,
                  })
                }
                className="group relative w-full block rounded-3xl overflow-hidden ring-1 ring-white/10 focus:outline-none focus-visible:ring-[var(--pam-gold)]"
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                data-testid={`food-slide-${it.id}`}
                aria-label={`Open ${it.name} in fullscreen`}
              >
                <div className="aspect-[4/5] sm:aspect-[16/12] overflow-hidden bg-[var(--pam-forest)]">
                  <img
                    src={it.img}
                    alt={`${it.name} — ${it.subtitle}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-5 md:p-7 text-left text-white">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.28em] text-[var(--pam-gold)]">
                        {it.subtitle}
                      </p>
                      <h4 className="mt-1.5 font-serif text-xl md:text-2xl">
                        {it.name}
                      </h4>
                    </div>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity w-10 h-10 rounded-full bg-white/15 backdrop-blur grid place-items-center">
                      <ZoomIn className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </motion.button>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-[3px] rounded-full transition-all ${
                selectedIndex === i
                  ? "w-8 bg-[var(--pam-gold)]"
                  : "w-4 bg-[var(--pam-forest)]/25 hover:bg-[var(--pam-forest)]/50"
              }`}
              data-testid={`food-dot-${i}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={scrollPrev}
            aria-label="Previous"
            className="w-11 h-11 rounded-full border border-[var(--pam-forest)]/25 hover:bg-[var(--pam-forest)] hover:text-white grid place-items-center transition-colors"
            data-testid="food-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Next"
            className="w-11 h-11 rounded-full border border-[var(--pam-forest)]/25 hover:bg-[var(--pam-forest)] hover:text-white grid place-items-center transition-colors"
            data-testid="food-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
